import { PROFILE_GENDER_CONVERTER, PROFILE_GRADE_CONVERTER, PROFILE_WORK_TYPE_CONVERTER } from '@constants/profile';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatDate, formatDateString, formatPhoneNumber } from '@utils/formatter';
import { useNavigate, useParams } from 'react-router-dom';

import { CustomButton } from '@components/Styles/ButtonStyle';
import { CustomStack } from '@components/Styles/CommonStyle';
import ProfileFormTable from '@components/common/Table/ProfileFormTable';
import ProfileViewTable from '@components/common/Table/ProfileViewTable';
import { request } from '@api/axios';
import { setProfileInfo } from './ProfileCreate';
import { useDialog } from '@context/DialogContext';
import { useLoading } from '@context/LoadingContext';
import { useReactToPrint } from 'react-to-print';

const ProfileDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isEdit, setIsEdit] = useState(false);
  const [profile, setProfile] = useState(null);
  const { showLoading, hideLoading } = useLoading();
  const { showDialog } = useDialog();

  const contentRef = useRef(null);
  const isFirstRender = useRef(true);
  const isDataFetched = useRef(false);

  const handlePrint = useReactToPrint({ contentRef: contentRef, documentTitle: `${profile?.profileData.name}_프로필` });

  const handleEdit = () => {
    setIsEdit((prev) => !prev);
  };

  const handleBackList = () => {
    navigate(-1);
  };

  const handleGetProfile = useCallback(async () => {
    if (isDataFetched.current && !isEdit) return;

    showLoading();
    try {
      const { data: response, status, message } = await request('get', `/api/profile/detail/${id}`);
      console.log('status', status, 'resData', response);

      if (status === 200) {
        const { profileData } = response;
        if (profileData) {
          profileData.formattedPhone = formatPhoneNumber(profileData.phone);

          profileData.formattedBirthDate = formatDateString(profileData.birthDate);

          profileData.convertedWorkType = PROFILE_WORK_TYPE_CONVERTER[profileData.workType] || '-';
          profileData.convertedGrade = PROFILE_GRADE_CONVERTER[profileData.grade] || '-';
          profileData.convertedGender = PROFILE_GENDER_CONVERTER[profileData.gender] || '-';

          profileData.formattedAcquisitionDate = formatDate(profileData.acquisitionYear, profileData.acquisitionMonth);
        }

        setProfile({ ...response, profileData });
        isDataFetched.current = true;
      } else {
        throw new Error(message);
      }
    } catch (error) {
      console.log(error);
      showDialog({ title: '확인', message: '조회에 실패했습니다.\n잠시 후 다시 시도해주세요.' });
    } finally {
      hideLoading();
    }
  }, [id, isEdit]);

  const handleUpdateProfile = useCallback(async (data) => {
    showLoading();
    try {
      const payload = setProfileInfo(data, showDialog);

      if (payload) {
        const { response, status, message } = await request('post', '/api/profile/update', payload);
        console.log('status', status, 'message', message);
        if (status === 200) {
          showDialog({
            title: '확인',
            message: '수정되었습니다.',
            showCloseButton: false,
            onConfirm: () => {
              handleEdit();
              isDataFetched.current = false; // 수정 후 데이터 재조회를 위해 플래그 리셋
              handleGetProfile();
            },
          });
        } else {
          throw new Error(message);
        }
      }
    } catch (error) {
      console.log(error);
      showDialog({ title: '확인', message: '수정에 실패했습니다.\n잠시 후 다시 시도해주세요.' });
    } finally {
      hideLoading();
    }
  }, []);

  useEffect(() => {
    if (id && isFirstRender.current) {
      handleGetProfile();
      isFirstRender.current = false;
    }
  }, [id]);

  return (
    <React.Fragment>
      {/* <Typography displayPrint="none">프로필 상세화면 {id}</Typography> */}
      {isEdit ? (
        <ProfileFormTable initialData={profile} onCancel={handleEdit} onSubmit={handleUpdateProfile} />
      ) : (
        profile && (
          <React.Fragment>
            <div ref={contentRef}>
              <ProfileViewTable data={profile} onPrint={handlePrint} />
            </div>

            <CustomStack justifyContent="center" mt={4}>
              <CustomButton color="gray" onClick={handleBackList}>
                목록
              </CustomButton>
              <CustomButton onClick={handleEdit}>수정하기</CustomButton>
            </CustomStack>
          </React.Fragment>
        )
      )}
    </React.Fragment>
  );
};

export default ProfileDetail;
